/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { INodeProps, Node, Module, Modal, ModalBody, ModalHeader, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISingleOrderTemplateViewProps } from './components';
import { IOrderTemplateListViewProps } from './krannich-c-order-template-list';
import {
    IKrannichCOrderTemplateListProps,
    IKrannichCOrderTemplateListResources
} from './krannich-c-order-template-list.props.autogenerated';
export interface ITemplateList {
    isMobile: boolean;
    orderTemplates: ISingleOrderTemplateViewProps[];
    OrderTemplate: INodeProps | undefined;
    toShowItemNumberProp: string | undefined;
    OrderTemplates: INodeProps | undefined;
    resources: IKrannichCOrderTemplateListResources;
}
export interface IOrderTemplateLine {
    orderTemplate: ISingleOrderTemplateViewProps;
    OrderTemplate?: INodeProps;
    resources?: IKrannichCOrderTemplateListResources;
}
const TemplateList: React.FC<ITemplateList> = ({
    isMobile,
    orderTemplates,
    OrderTemplate,
    toShowItemNumberProp,
    OrderTemplates,
    resources
}) => {
    if (!orderTemplates) {
        return null;
    }
    //*******VSI CUSTOMIZATION START*******//

    //Customization States and UseEffects
    const [templates, setTemplates] = React.useState<ISingleOrderTemplateViewProps[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isDisabled, setIsDisabled] = React.useState<boolean>(false);
    const [counter, setCounter] = React.useState<number>(1);
    const [quan, setQuan] = React.useState<number>(Number(toShowItemNumberProp) || 5);
    const [toShowItemNumber, setToShowItemNumber] = React.useState(quan);

    //First time call to set number of items set to display from config otherwise default 2 and call addAddress function to set
    //addresses
    React.useEffect(() => {
        setQuan(Number(toShowItemNumberProp) || 5);
        addAddresses();
    }, []);

    React.useEffect(() => {
        setQuan(Number(toShowItemNumberProp) || 5);
        addAddresses();
    }, [orderTemplates]);

    //Call after every change in toShwoItemNumber
    React.useEffect(() => {
        addAddresses();
    }, [toShowItemNumber]);

    //Disable viewmore button once TSX prop "items" arrary is equal to our custom addreses array
    React.useEffect(() => {
        orderTemplates.length === templates.length ? setIsDisabled(true) : setIsDisabled(false);
    }, [orderTemplates, templates]);

    //Update to display item number
    React.useEffect(() => {
        setToShowItemNumber(quan * counter);
    }, [counter]);

    //Function is used to set counter on every click on view more button
    const handleToShowItemNumer = () => {
        setCounter(counter + 1);
    };

    //Funciton is used to set the items in our custom array according to view more button clicks and default config dispaly number
    const addAddresses = () => {
        setIsLoading(true);
        const arr: ISingleOrderTemplateViewProps[] = [];
        for (let index = 0; index < toShowItemNumber; ++index) {
            // eslint-disable-next-line security/detect-object-injection
            orderTemplates[index] && arr.push(orderTemplates[index]);
        }
        setTemplates(arr);
        setIsLoading(false);
    };
    //*******VSI CUSTOMIZATION END*******//
    if (isMobile) {
        return (
            <>
                <Node {...OrderTemplates!}>
                    {templates &&
                        templates?.map(orderTemplate => {
                            return (
                                <RenderOrderTemplateForMobile
                                    orderTemplate={orderTemplate}
                                    OrderTemplate={OrderTemplate}
                                    key={orderTemplate.key}
                                />
                            );
                        })}
                </Node>

                <button
                    className={`ms-mobile-order-template__btn-more  msc-btn ${isDisabled ? 'ctx-disabled-view-more' : ''}`}
                    onClick={() => {
                        handleToShowItemNumer();
                    }}
                    disabled={isDisabled}
                >
                    {resources?.viewMoreButton}
                </button>
            </>
        );
    } else {
        return (
            <>
                {isLoading ? (
                    <Waiting />
                ) : (
                    <>
                        <Node {...OrderTemplates!}>
                            <li className='order-template-lines-container'>
                                <span className='order-template-name'>{resources?.orderTemplateName}</span>
                                <span className='order-template-lines-no'>{resources?.productsInTemplate}</span>
                                <span className='order-template-actions'>{resources?.actionsHeadingTemplate}</span>
                            </li>
                            {templates &&
                                templates?.map(orderTemplate => {
                                    return (
                                        <RenderOrderTemplate
                                            orderTemplate={orderTemplate}
                                            OrderTemplate={OrderTemplate}
                                            key={orderTemplate.key}
                                            resources={resources}
                                        />
                                    );
                                })}
                        </Node>

                        <button
                            className={`ms-order-template__btn-more  msc-btn ${isDisabled ? 'ctx-disabled-view-more' : ''}`}
                            onClick={() => {
                                handleToShowItemNumer();
                            }}
                            disabled={isDisabled}
                        >
                            {resources?.viewMoreButton}
                        </button>
                    </>
                )}
            </>
        );
    }
};
const OrderTemplateListView: React.FC<IOrderTemplateListViewProps & IKrannichCOrderTemplateListProps<{}>> = props => {
    const {
        OrderTemplateList,
        status,
        statusMessage,
        heading,
        OrderTemplates,
        orderTemplates,
        OrderTemplate,
        createOrderTemplateButton,
        templateHeaderStatusMessage,
        isMobile,
        resources,
        toShowItemNumber
    } = props;
    if (isMobile) {
        return (
            <Module {...OrderTemplateList}>
                {heading}
                {status !== 'SUCCESS' && statusMessage}
                <Node className=''>
                    {createOrderTemplateButton}
                    {templateHeaderStatusMessage}
                </Node>
                {OrderTemplates && orderTemplates && orderTemplates.length > 0 && (
                    <TemplateList
                        isMobile={true}
                        orderTemplates={orderTemplates}
                        OrderTemplate={OrderTemplate}
                        toShowItemNumberProp={toShowItemNumber}
                        OrderTemplates={OrderTemplates}
                        resources={resources}
                    />
                )}
            </Module>
        );
    } else {
        return (
            <Module {...OrderTemplateList}>
                {heading}
                {status !== 'SUCCESS' && statusMessage}
                <Node className=''>
                    {createOrderTemplateButton}
                    {templateHeaderStatusMessage}
                </Node>
                {OrderTemplates && orderTemplates && orderTemplates.length > 0 && (
                    <TemplateList
                        isMobile={false}
                        orderTemplates={orderTemplates}
                        OrderTemplate={OrderTemplate}
                        toShowItemNumberProp={toShowItemNumber}
                        OrderTemplates={OrderTemplates}
                        resources={resources}
                    />
                )}
            </Module>
        );
    }
};

const RenderOrderTemplate: React.FC<IOrderTemplateLine> = ({ orderTemplate, OrderTemplate, resources }) => {
    const { key, orderTemplateStatusMessage, removeButton, orderTemplateName } = orderTemplate;

    const [showAlert, setShowAlert] = React.useState<boolean>(false);
    const toggleAlert = () => {
        setShowAlert(!showAlert);
    };
    return (
        <Node key={key} className={OrderTemplate!.className} {...OrderTemplate}>
            {showAlert && (
                <Modal
                    wrapClassName='ms-order-template-mobile-view-modal buybox-description-popup-container'
                    autoFocus
                    fade={false}
                    applicationNode='renderPage'
                    isOpen={true}
                    toggle={toggleAlert}
                >
                    <div className='alert-modal-body-container'>
                        <ModalHeader toggle={toggleAlert}>{resources?.deleteTemplateHeading}</ModalHeader>
                        <ModalBody>
                            <span>{resources?.deleteSelectedOrderTemplate}</span>
                            <div className='alert-btn-group'>
                                {removeButton}
                                <button className='msc-delete-orderTemplateList-btn btn cancel-btn ' onClick={toggleAlert}>
                                    {resources?.cancelButton}
                                </button>
                            </div>
                        </ModalBody>
                    </div>
                </Modal>
            )}
            {orderTemplateName}
            <div className='clear-cart'>
                <button onClick={toggleAlert}>x</button>
            </div>
            {orderTemplateStatusMessage}
        </Node>
    );
};

const RenderOrderTemplateForMobile: React.FC<IOrderTemplateLine> = ({ orderTemplate, resources }) => {
    const { orderTemplateStatusMessage, removeButton, orderTemplateName } = orderTemplate;
    const [showAlert, setShowAlert] = React.useState<boolean>(false);
    const toggleAlert = () => {
        setShowAlert(!showAlert);
    };
    return (
        <div className='order-template-block'>
            {showAlert && (
                <Modal
                    wrapClassName='ms-order-template-mobile-view-modal buybox-description-popup-container'
                    autoFocus
                    fade={false}
                    applicationNode='renderPage'
                    isOpen={true}
                    toggle={toggleAlert}
                >
                    <div className='alert-modal-body-container'>
                        <ModalHeader toggle={toggleAlert}>{resources?.deleteTemplateHeading}</ModalHeader>
                        <ModalBody>
                            <span>{resources?.deleteSelectedOrderTemplate}</span>
                            <div className='alert-btn-group'>
                                {removeButton}
                                <button className='msc-delete-orderTemplateList-btn btn cancel-btn ' onClick={toggleAlert}>
                                    {resources?.cancelButton}
                                </button>
                            </div>
                        </ModalBody>
                    </div>
                </Modal>
            )}
            <div className='_title-container'>{orderTemplateName}</div>

            <div className='clear-cart'>
                <button onClick={toggleAlert}>x</button>
            </div>
            {orderTemplateStatusMessage}
        </div>
    );
};

export default OrderTemplateListView;
